/**
 * Invoice helpers — shared by InvoicePrint and (lightly) by the Receipt.
 *
 * Everything here is pure/stateless so it's easy to reuse from both the
 * post-confirmation "pro-forma" invoice (folio has no charges yet) and the
 * post-checkin invoice (folio has real charges).
 */

// ─── Number → words ────────────────────────────────────────────────────────
const ONES = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten',
  'Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
const TENS = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
const SCALES = ['','Thousand','Million','Billion','Trillion'];

function threeDigitsToWords(n) {
  let str = '';
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds) str += `${ONES[hundreds]} Hundred`;
  if (rest) {
    if (str) str += ' ';
    if (rest < 20) str += ONES[rest];
    else {
      const t = Math.floor(rest / 10), o = rest % 10;
      str += TENS[t] + (o ? `-${ONES[o]}` : '');
    }
  }
  return str;
}

/** Integer → English words. e.g. 145632 -> "One Hundred Forty-Five Thousand Six Hundred Thirty-Two" */
export function numberToWords(num) {
  let n = Math.floor(Math.abs(Number(num) || 0));
  if (n === 0) return 'Zero';
  const groups = [];
  while (n > 0) { groups.push(n % 1000); n = Math.floor(n / 1000); }
  const parts = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    if (!groups[i]) continue;
    const words = threeDigitsToWords(groups[i]);
    parts.push(i > 0 ? `${words} ${SCALES[i]}` : words);
  }
  return parts.join(' ');
}

/** Currency code -> { plural, minor } words. Falls back to the raw code if unknown. */
export const CURRENCY_WORDS = {
  USD:{ plural:'US Dollars',           minor:'Cents'    },
  EUR:{ plural:'Euros',                minor:'Cents'    },
  RWF:{ plural:'Rwandan Francs',       minor:'Centimes' },
  KES:{ plural:'Kenyan Shillings',     minor:'Cents'    },
  TZS:{ plural:'Tanzanian Shillings',  minor:'Cents'    },
  UGX:{ plural:'Ugandan Shillings',    minor:'Cents'    },
  ETB:{ plural:'Ethiopian Birr',       minor:'Cents'    },
  ZAR:{ plural:'South African Rand',   minor:'Cents'    },
  GBP:{ plural:'British Pounds',       minor:'Pence'    },
  AED:{ plural:'UAE Dirhams',          minor:'Fils'     },
  NGN:{ plural:'Nigerian Naira',       minor:'Kobo'     },
};

/** e.g. amountInWords(177000,'RWF') -> "One Hundred Seventy-Seven Thousand Rwandan Francs" */
export function amountInWords(amount, currencyCode) {
  const cur   = CURRENCY_WORDS[currencyCode] || { plural: currencyCode || 'Units', minor: 'Cents' };
  const abs   = Math.abs(Number(amount) || 0);
  const whole = Math.floor(abs);
  const minor = Math.round((abs - whole) * 100);
  let words = `${numberToWords(whole)} ${cur.plural}`;
  if (minor > 0) words += ` and ${numberToWords(minor)} ${cur.minor}`;
  return words;
}

// ─── Number formatting (no currency symbol — used inside table cells) ─────
export function fmtNum(n) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));
}

const round2 = n => Math.round((Number(n) || 0) * 100) / 100;

/**
 * RRA/EBM-compliant accommodation tax extraction.
 *
 * Accommodation amounts are VAT-inclusive. The RRA specifies a two-step
 * sequential extraction — Tourism Levy is NOT a percentage of the gross
 * amount; it is derived from the extracted VAT:
 *
 *   Step 1 — Extract VAT (gross is VAT-inclusive at vatRate %):
 *     VAT = grossAmount × vatRate / (100 + vatRate)
 *     e.g. 5,000 × 18 / 118 = 762.71
 *
 *   Step 2 — Derive Tourism Levy from the extracted VAT:
 *     TourismLevy = VAT × tourismRate / (100 + tourismRate)
 *     e.g. 762.71 × 3 / 103 = 22.21
 *
 *   Net = grossAmount − VAT − TourismLevy
 *
 * This function is the single authoritative implementation of these formulas
 * on the frontend. All display layers (InvoicePrint, FolioSheet, CorporateSessions)
 * delegate here so the calculation is consistent everywhere.
 */
export function splitAccommodationTax(grossAmount, tourismRatePct, vatRatePct) {
  const tourismRate = Math.max(0, Number(tourismRatePct) || 0);
  const vatRate     = Math.max(0, Number(vatRatePct)     || 0);
  if (vatRate <= 0 && tourismRate <= 0) return { net: grossAmount, tourismTax: 0, vat: 0 };

  // Step 1: extract VAT from the VAT-inclusive gross amount
  //   VAT = Amount × vatRate / (100 + vatRate)
  //   e.g. 5,000 × 18 / 118 = 762.71
  const vat = vatRate > 0 ? round2(grossAmount * vatRate / (100 + vatRate)) : 0;

  // Step 2: Tourism Levy is calculated on the amount AFTER removing VAT
  //   (the "net-of-VAT" figure), NOT on the VAT amount itself.
  //   netOfVat  = Amount − VAT
  //   Tourism   = netOfVat × tourismRate / (100 + tourismRate)
  //   e.g. (5,000 − 762.71) × 3 / 103 = 4,237.29 × 3/103 = 123.42
  const netOfVat   = round2(grossAmount - vat);
  const tourismTax = (tourismRate > 0 && netOfVat > 0)
    ? round2(netOfVat * tourismRate / (100 + tourismRate))
    : 0;

  const net = round2(grossAmount - vat - tourismTax);
  return { net, tourismTax, vat };
}

// ─── Tax breakdown ──────────────────────────────────────────────────────────
/**
 * @deprecated kept only so nothing else importing this breaks. Superseded by
 * computeInvoiceTaxFromCharges, which sums each charge's own taxAmount
 * (computed server-side by the category-based tax engine — Accommodation vs
 * Other Services) instead of reverse-engineering a single blended rate
 * across the whole invoice. The old approach applied VAT to the *entire*
 * gross total including room charges, which is wrong as soon as a stay has
 * both accommodation (3%) and other services (18%) — those are taxed at
 * different rates and must be kept separate, not blended.
 */
export function computeInvoiceTax(grossTotal, vatRatePct, tourismRatePct) {
  const gross       = round2(grossTotal);
  const vatRate      = Math.max(0, Number(vatRatePct)      || 0) / 100;
  const tourismRate   = Math.max(0, Number(tourismRatePct)  || 0) / 100;

  const base       = vatRate > 0 ? gross / (1 + vatRate) : gross;
  const vat        = round2(gross - base);
  const tourismTax = Math.floor(base * tourismRate / (1 + tourismRate));
  const netSales    = round2(base - tourismTax);
  const totalTax    = round2(vat + tourismTax);

  return { netSales, vat, tourismTax, totalTax, grossTotal: gross, vatRatePct: Number(vatRatePct)||0, tourismRatePct: Number(tourismRatePct)||0 };
}

/** Human-readable label for each tax code shown on the invoice breakdown. */
const TAX_CODE_LABELS = {
  TOURISM_LEVY: 'Tourism Tax (Accommodation)',
  VAT:          'VAT (Services)',
  MANUAL:       'Tax',
};

/**
 * Sums each charge's own (server-computed) taxAmount, grouped by taxCode,
 * instead of reverse-engineering a blended rate from the gross total. This
 * is the correct approach now that every FolioCharge carries its own
 * category-based tax (Accommodation = Tourism Levy + VAT combined, Other
 * Services = VAT only) — a stay mixing room charges and restaurant charges
 * will correctly show separate tax lines at the right rates, rather than
 * one blended percentage applied to everything.
 *
 * Charges store amount = GROSS (tax-inclusive) — exactly what the guest is
 * charged/pays, unmodified from what staff entered. taxAmount is purely an
 * informational breakdown already embedded inside that amount, extracted
 * for display, so it is never added on top when computing the total.
 *
 * Accommodation charges carry taxCode 'TOURISM_VAT' — a combined 3%+18%
 * figure — which this function splits back into separate "Tourism Tax
 * (Accommodation)" and "VAT (Services)" lines using the property's current
 * rates, so the breakdown always shows both taxes distinctly even though
 * only one combined number is stored per charge.
 *
 * @param charges  array of FolioCharge-shaped objects: {amount, taxAmount, taxCode, voided}
 * @param property the property, used to split combined accommodation tax into
 *                  its Tourism Levy / VAT components (tourismTaxRate, vatRate)
 */
export function computeInvoiceTaxFromCharges(charges, property) {
  const active = (charges || []).filter(c => !c.voided);
  let grossTotal = 0;
  const byCode = new Map(); // taxCode -> { taxable, tax }
  const tourismRate = property?.tourismTaxRate != null ? Number(property.tourismTaxRate) : 0;
  const vatRate      = property?.vatRate       != null ? Number(property.vatRate)       : 0;

  const addToCode = (code, taxable, tax) => {
    const entry = byCode.get(code) || { taxable: 0, tax: 0 };
    entry.taxable = round2(entry.taxable + taxable);
    entry.tax     = round2(entry.tax + tax);
    byCode.set(code, entry);
  };

  for (const c of active) {
    const amt = round2(parseFloat(c.amount || 0));
    const tax = round2(parseFloat(c.taxAmount || 0));
    grossTotal = round2(grossTotal + amt);
    if (tax <= 0) continue;

    if (c.taxCode === 'TOURISM_VAT') {
      // Combined accommodation tax — split into its two components, both
      // computed on the same tax-exclusive net base.
      const { net, tourismTax, vat } = splitAccommodationTax(amt, tourismRate, vatRate);
      if (tourismTax > 0) addToCode('TOURISM_LEVY', net, tourismTax);
      if (vat > 0)        addToCode('VAT', net, vat);
    } else {
      const code = c.taxCode || 'TAX';
      addToCode(code, round2(amt - tax), tax);
    }
  }

  const breakdown = [...byCode.entries()].map(([code, v]) => ({
    code,
    label: TAX_CODE_LABELS[code] || code,
    taxable: v.taxable,
    tax: v.tax,
    ratePct: v.taxable > 0 ? round2((v.tax / v.taxable) * 100) : 0,
  }));

  const totalTax   = round2(breakdown.reduce((a, b) => a + b.tax, 0));
  const netSales    = round2(grossTotal - totalTax);

  return { netSales, breakdown, totalTax, grossTotal };
}

/**
 * Builds a synthetic charge-shaped tax input for the pre-checkin "projected"
 * invoice, when no real FolioCharge rows exist yet. Mirrors the backend's
 * default classification (room = Accommodation, taxed at Tourism Levy + VAT
 * combined) so the projected invoice's tax line matches exactly what will be
 * posted once the nightly room charge actually runs. ratePerNight is quoted
 * tax-inclusive, so the tax portion is extracted out of it rather than added
 * on top.
 */
export function projectedTaxableCharge(reservation, property) {
  const nights = parseInt(reservation?.numNights || 0, 10) || 0;
  const rate    = parseFloat(reservation?.ratePerNight || 0) || 0;
  const amount  = round2(rate * nights);
  if (!amount) return [];
  const tourismRate = property?.tourismTaxRate != null ? Number(property.tourismTaxRate) : 0;
  const vatRate      = property?.vatRate       != null ? Number(property.vatRate)       : 0;

  // Use the RRA two-step formula — same as splitAccommodationTax — so the
  // projected invoice matches exactly what the backend will post when the
  // nightly room-charge scheduler runs.
  const { vat, tourismTax } = splitAccommodationTax(amount, tourismRate, vatRate);
  const taxAmount = round2(vat + tourismTax);

  return [{
    amount,
    taxCode: 'TOURISM_VAT',
    taxAmount,
    voided: false,
  }];
}

// ─── Charge grouping (turns folio charges into invoice line items) ─────────
const CHARGE_TYPE_LABELS = {
  room:'Accommodation', restaurant:'Restaurant', spa:'Spa & Wellness',
  minibar:'Minibar', parking:'Parking', laundry:'Laundry',
  phone:'Phone / Internet', transport:'Transport', tax:'Tax / Levy', other:'Miscellaneous',
};
export const chargeTypeLabel = type => CHARGE_TYPE_LABELS[type] || type || 'Charge';

/**
 * Groups folio charges into invoice line items with Description / Quantity /
 * Unit Price / Total — exactly the data already shown on the folio receipt,
 * just re-shaped into a tabular, dynamic-per-business invoice format.
 *
 * Room charges are always grouped into one "Accommodation" line (nightly
 * room charges share a description that differs only by date, so grouping
 * purely by chargeType+amount turns N identical nightly charges into a
 * single "Accommodation — N Night(s) @ rate" row). Every other charge type
 * is grouped by (type + description + amount), so e.g. three identical
 * "Bed & Breakfast" charges of the same amount become one row with Qty 3,
 * while distinct one-off charges (Restaurant, Pool, etc.) stay separate.
 *
 * `numNights`, when provided, is the same trusted value the guest receipt
 * displays directly (folio.numNights, falling back to reservation.numNights)
 * — see ReceiptPrint in FolioSheet.jsx. We align the Accommodation row's
 * quantity to it whenever they disagree, rather than only in the narrow
 * case of a single lump-sum "manual" room-charge record. This guarantees
 * the invoice and the receipt can never show a different number of nights
 * for the same stay. The (unchanged) total is spread evenly across
 * `numNights` to keep the unit price consistent with the displayed quantity.
 */
export function groupChargesForInvoice(charges, roomTypeName, numNights) {
  const active = (charges || []).filter(c => !c.voided);
  const order = [];
  const byKey = new Map();

  for (const c of active) {
    // c.amount is already the full, tax-inclusive charge — taxAmount is just
    // an informational breakdown of it, not an additional amount to collect.
    const unitPrice = round2(parseFloat(c.amount || 0));
    const isRoom = c.chargeType === 'room';
    const key = isRoom
      ? 'room'
      : `${c.chargeType}|${(c.description || '').trim().toLowerCase()}|${unitPrice}`;

    if (!byKey.has(key)) {
      const label = isRoom
        ? `Accommodation${roomTypeName ? ` (${roomTypeName})` : ''}`
        : (c.description?.trim() || chargeTypeLabel(c.chargeType));
      const group = { key, label, unit: isRoom ? 'Night' : '', unitPrice, quantity: 0, total: 0, isRoom };
      byKey.set(key, group);
      order.push(group);
    }
    const g = byKey.get(key);
    g.quantity += 1;
    g.total = round2(g.total + unitPrice);
  }

  const nights = parseInt(numNights || 0, 10) || 0;
  const roomGroup = byKey.get('room');
  if (roomGroup && nights > 0 && roomGroup.quantity !== nights) {
    roomGroup.quantity = nights;
    roomGroup.unitPrice = round2(roomGroup.total / nights);
  }

  return order;
}

/**
 * Fallback line items for an invoice printed right after confirmation,
 * before any folio charges have been posted (e.g. before check-in, when the
 * nightly room-charge scheduler hasn't run yet). Projects the accommodation
 * cost from the reservation's own rate/nights so the "Invoice" button is
 * useful immediately at confirmation, as requested — not just post-checkin.
 */
export function buildProjectedLineItems(reservation) {
  const nights = parseInt(reservation?.numNights || 0, 10) || 0;
  const rate   = parseFloat(reservation?.ratePerNight || 0) || 0;
  if (!nights || !rate) return [];
  return [{
    key: 'projected-room',
    label: `Accommodation${reservation?.roomTypeName ? ` (${reservation.roomTypeName})` : ''}`,
    unit: 'Night',
    unitPrice: rate,
    quantity: nights,
    total: round2(rate * nights),
    isRoom: true,
  }];
}

// ─── Configurable document templates (Proforma Terms / Payment Request) ────
/**
 * Built-in fallback Terms & Conditions for the Proforma Invoice, used
 * whenever a property (or a specific corporate account) hasn't configured
 * its own. Hotels edit this in Property Setup → Invoicing & Tax, or
 * per-company in Corporate Accounts → Advanced.
 */
export const DEFAULT_PROFORMA_TERMS =
  '50% of the total amount must be paid to confirm the reservation, with the balance settled on the day of ' +
  'check-in or the function, for guests without a contract with the hotel. Extras are to be settled on departure.\n' +
  'No reservation will be held or confirmed in our system until a Local Purchase Order (LPO) is received from ' +
  'clients with a contract with the hotel. This should be done 30 days before the function.\n' +
  'Cancellations will only be accepted in writing. Cancellations made 5 days or less before the arrival or ' +
  'function date will be charged in full.\n' +
  'Accepted methods of payment: Credit Card, Cash, Electronic Transfer.';

/**
 * Built-in fallback body paragraph for the Payment Request letter. Supports
 * the same {placeholder} merge fields as a custom template — see
 * renderTemplate below.
 */
export const DEFAULT_PAYMENT_REQUEST_BODY =
  '{company} owes {property} the sum of {amountWords} ({amountNumeric} {currency}) with Total Tax of ' +
  '{totalTax} {currency} inclusive, for {reason}, all attached on invoice number {invoiceNumber}.';

/**
 * Substitutes {placeholder} merge fields in a template string. Unknown
 * placeholders are left as-is rather than silently dropped, so a typo in a
 * hotel's custom template is visible instead of producing missing text.
 */
export function renderTemplate(template, fields) {
  return (template || '').replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(fields, key) ? String(fields[key]) : match);
}